import { useInfiniteQuery } from "@tanstack/react-query";
import { round } from "lodash-es";
import { useCallback, useMemo, useState } from "react";
import { twJoin } from "tailwind-merge";

import type { CommunityGroupDetailsV2Dto, UserExtendedDto } from "@/api/types";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { List } from "@/components/List/List";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { TagList } from "@/components/Tag/TagList";
import { User } from "@/components/User/User";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { useCommunityGroupQueries } from "@/queries/community-groups/queryOptions";
import { Trans, useTranslation } from "@/translations";

import { canViewGroupMembers } from "../permissions";

const MIN_SEARCH_CHARACTERS = 2;
type GroupMembersModalProps = ModalBaseProps & {
  group: CommunityGroupDetailsV2Dto;
};
export function GroupMembersModal({ group, isOpened, onOpenChange }: GroupMembersModalProps): React.ReactNode {
  const { t } = useTranslation();
  const [query, setQuery] = useState<string>("");
  const debouncedQuery = useDebounceValue(query.trim().length < MIN_SEARCH_CHARACTERS ? "" : query);
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();
  const communityGroupQueries = useCommunityGroupQueries();
  const {
    data: availableUsersData,
    hasNextPage: hasMoreUsers,
    fetchNextPage: fetchMoreUsers,
    isLoading: isLoadingUsers,
    isFetchingNextPage: isFetchingMoreUsers,
  } = useInfiniteQuery({
    ...communityGroupQueries.membersInfinite(group.id, debouncedQuery),
    enabled: isOpened,
  });
  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreUsers && hasMoreUsers) {
        void fetchMoreUsers();
      }
    }, [fetchMoreUsers, hasMoreUsers, isFetchingMoreUsers]),
  });
  const availableUsers = useMemo(() => availableUsersData?.pages.flatMap((x) => x.items) ?? [], [availableUsersData]);

  return (
    <Modal.Root
      title={t("page.group-details.members-modal.title")}
      {...{
        isOpened,
        onOpenChange,
      }}
    >
      <Modal.Body>
        {sessionUser.isAdmin && (
          <Stack gap="sm">
            {hasPermission(canViewGroupMembers) &&
              !group.disabled &&
              group.joinedResidents != null &&
              group.totalResidentsAudience != null && (
                <span className="text-caption text-text-secondary">
                  <Trans
                    i18nKey={
                      group.type === "helpCategory"
                        ? "model.help-category.audience.details"
                        : "model.interest-group.audience.details"
                    }
                    values={{
                      percentage:
                        group.totalResidentsAudience !== 0
                          ? round((group.joinedResidents / group.totalResidentsAudience) * 100, 1)
                          : 0,
                      current: group.joinedResidents,
                      total: group.totalResidentsAudience,
                    }}
                    components={{
                      bold: <strong className="text-caption-emphasis" />,
                    }}
                  />
                </span>
              )}
            <section
              className={twJoin(
                "flex items-center gap-space-sm text-text-secondary",
                group.disabled && "text-text-secondary",
              )}
            >
              <Users01Icon />
              {group.audience?.length ? (
                <TagList
                  tags={group.audience?.map(({ key, ...item }) => ({
                    content: key,
                    ...item,
                  }))}
                  limit={3}
                />
              ) : (
                <i>
                  {group.type === "helpCategory"
                    ? t("model.help-category.audience.placeholder")
                    : t("model.interest-group.audience.placeholder")}
                </i>
              )}
            </section>
          </Stack>
        )}
        <Stack gap="md" data-testid="group-members-modal">
          <SearchInput
            data-testid="search-input"
            onChange={(e) => setQuery(e.target.value.trim())}
            placeholder={t("page.group-details.members-modal.search-placeholder")}
          />
          <div className="max-h-scroll-area-compact overflow-y-scroll rounded-md">
            <GroupMembersUserList isLoading={isLoadingUsers} users={availableUsers} />
            {hasMoreUsers && <CollectionLoadingState ref={ref} />}
          </div>
        </Stack>
      </Modal.Body>
    </Modal.Root>
  );
}
function GroupMembersUserList({ isLoading, users }: { isLoading: boolean; users: UserExtendedDto[] }): React.ReactNode {
  const { t } = useTranslation();

  if (isLoading) {
    return <CollectionLoadingState />;
  }
  if (users.length === 0) {
    return (
      <p className="px-card-compact pt-space-xs pb-space-md text-caption text-text-secondary">
        {t("page.group-details.members-modal.no-results")}
      </p>
    );
  }

  return (
    <List getKey={(user) => user.id} items={users}>
      {(user) => <User user={user} renderSubtitle={() => user.locatedAt} />}
    </List>
  );
}
